import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AcademicCapIcon, PlusIcon, CalendarIcon, MapPinIcon, UserGroupIcon, TrashIcon, EyeIcon, XMarkIcon, PencilIcon, MagnifyingGlassIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// ⚠️ IMPORTANT: Replace this URL with your deployed Google Apps Script Web App URL
// Get this URL after deploying the TrainingAPI.gs script
const GOOGLE_APPS_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbz2KjNy-qFTfSKmQVjaRxOYXOEHcgNVyHSC9T9sZmwQdMjaBrBuLrk7_Eyj9rl9Uo6XHA/exec';

const TrainingManagement = () => {
    const [loading, setLoading] = useState(false);
    const [trainings, setTrainings] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showViewDialog, setShowViewDialog] = useState(false);
    const [showBulkSentByDialog, setShowBulkSentByDialog] = useState(false);
    const [tempSelectedEmployees, setTempSelectedEmployees] = useState([]);
    const [bulkSentBy, setBulkSentBy] = useState('');
    const [bulkNeedsRoom, setBulkNeedsRoom] = useState(false);
    const [selectedTraining, setSelectedTraining] = useState(null);
    const [sentByFilter, setSentByFilter] = useState('');
    const [roomFilter, setRoomFilter] = useState('all'); // 'all', 'needs', 'no-needs'
    const [formData, setFormData] = useState({ id: null, title: '', startDate: '', endDate: '', location: '', description: '', mapIframe: '', selectedEmployees: [] });

    useEffect(() => { loadEmployees(); loadTrainings(); }, []);
    useEffect(() => {
        if (searchTerm) {
            setFilteredEmployees(employees.filter(emp => `${emp.firstName} ${emp.lastName} ${emp.position}`.toLowerCase().includes(searchTerm.toLowerCase())));
        } else {
            setFilteredEmployees(employees);
        }
    }, [searchTerm, employees]);

    const loadEmployees = async () => {
        try {
            const response = await axios({
                method: 'get',
                url: 'https://www.phoenix-md.com/api/personnel/employee/read',
                params: { not: '"แพทย์"' },
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': localStorage.getItem('token')
                }
            });
            if (response.data?.results) {
                setEmployees(response.data.results);
                setFilteredEmployees(response.data.results);
            } else if (response.data?.data) {
                setEmployees(response.data.data);
                setFilteredEmployees(response.data.data);
            } else if (Array.isArray(response.data)) {
                setEmployees(response.data);
                setFilteredEmployees(response.data);
            }
        } catch (error) {
            console.error('Error loading employees:', error);
            Swal.fire({ icon: 'error', title: 'ไม่สามารถโหลดข้อมูลพนักงาน', text: error.message, confirmButtonColor: '#EF4444' });
        }
    };

    const loadTrainings = async () => {
        setLoading(true);
        try {
            const response = await fetch(GOOGLE_APPS_SCRIPT_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({ action: 'list' })
            });

            const data = await response.json();

            if (data.success && data.data) {
                // Parse participants JSON string back to array และแปลงวันที่
                const trainingsWithParsedParticipants = data.data.map(training => {
                    let participants = [];
                    try {
                        participants = typeof training.participants === 'string'
                            ? JSON.parse(training.participants)
                            : training.participants || [];
                    } catch (e) {
                        console.error('Error parsing participants:', e);
                        participants = [];
                    }

                    // ใช้วันที่จากฐานข้อมูลโดยตรง ไม่แปลงผ่าน Date object เพื่อหลีกเลี่ยงปัญหา timezone
                    // ถ้าเป็น ISO format (YYYY-MM-DDTHH:mm:ss.sssZ) ให้ตัดเอาแค่ YYYY-MM-DD
                    const options = { timeZone: 'Asia/Bangkok', year: 'numeric', month: '2-digit', day: '2-digit' };

                    // ผลลัพธ์ที่ได้จะเป็นฟอร์แมต YYYY-MM-DD ตามเวลาไทย
                    const startDate = training.startDate
                        ? new Date(training.startDate).toLocaleDateString('en-CA', options)
                        : '';

                    const endDate = training.endDate
                        ? new Date(training.endDate).toLocaleDateString('en-CA', options)
                        : '';

                    return {
                        ...training,
                        startDate,
                        endDate,
                        participants
                    };
                });
                setTrainings(trainingsWithParsedParticipants);
            } else {
                setTrainings([]);
            }
        } catch (error) {
            console.error('Error loading trainings:', error);
            Swal.fire({
                icon: 'error',
                title: 'ไม่สามารถโหลดข้อมูลการอบรม',
                text: error.message,
                confirmButtonColor: '#EF4444'
            });
            setTrainings([]);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => { setFormData({ id: null, title: '', startDate: '', endDate: '', location: '', description: '', mapIframe: '', selectedEmployees: [] }); setSearchTerm(''); };
    const handleOpenCreateDialog = () => { resetForm(); setShowCreateDialog(true); };
    const handleOpenEditDialog = (training) => {
        setFormData({
            id: training.id,
            title: training.title,
            startDate: training.startDate,
            endDate: training.endDate,
            location: training.location,
            description: training.description,
            mapIframe: training.mapIframe || '',
            selectedEmployees: training.participants || [] // โหลดคนเดิมมา เพื่อให้เพิ่มคนใหม่เข้าไปได้
        });
        setShowEditDialog(true);
    };
    const handleCloseDialogs = () => { setShowCreateDialog(false); setShowEditDialog(false); setShowViewDialog(false); setShowBulkSentByDialog(false); setTempSelectedEmployees([]); resetForm(); };
    const handleInputChange = (field, value) => { setFormData(prev => ({ ...prev, [field]: value })); };

    const toggleEmployeeSelection = (employee) => {
        // ใช้ ID หลักของพนักงาน (id หรือ _id)
        const employeeId = employee.id || employee._id;

        // ตรวจสอบว่าเลือกแล้วหรือยัง
        const isSelected = tempSelectedEmployees.some(emp => {
            const empId = emp.id || emp._id;
            return empId === employeeId;
        });

        if (isSelected) {
            // ยกเลิกการเลือก - ลบออกจาก temp
            setTempSelectedEmployees(prev => prev.filter(emp => {
                const empId = emp.id || emp._id;
                return empId !== employeeId;
            }));
        } else {
            // เพิ่มเข้าไปใน temp
            setTempSelectedEmployees(prev => [...prev, employee]);
        }
    };

    const handleOpenBulkSentByDialog = () => {
        if (tempSelectedEmployees.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'กรุณาเลือกพนักงาน',
                text: 'กรุณาเลือกพนักงานอย่างน้อย 1 คน',
                confirmButtonColor: '#4F46E5'
            });
            return;
        }
        setBulkSentBy('');
        setBulkNeedsRoom(false);
        setShowBulkSentByDialog(true);
    };

    const handleConfirmBulkSentBy = () => {
        if (!bulkSentBy.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'กรุณากรอกข้อมูล',
                text: 'กรุณาระบุว่าส่งโดยใคร',
                confirmButtonColor: '#4F46E5'
            });
            return;
        }

        // เพิ่มข้อมูล sentBy และ needsRoom ให้กับพนักงานที่เลือกทั้งหมด
        // เก็บเฉพาะฟิลด์ที่ต้องการ
        const employeesWithDetails = tempSelectedEmployees.map(emp => ({
            // ข้อมูลพื้นฐาน
            id: emp.id || emp._id,
            firstName: emp.firstName || '',
            lastName: emp.lastName || '',
            MedicalLicenseID: emp.MedicalLicenseID || '',
            address: emp.address || '',
            codeAddress: emp.codeAddress || '',
            dob: emp.dob || '',
            councilID: emp.councilID || '',
            firstDayOfWork: emp.firstDayOfWork || '',
            WorID: emp.WorID || '',
            phone: emp.phone || '',
            emailNurse: emp.emailNurse || '',
            nickname: emp.nickname || '',
            sex: emp.sex || '',
            trtCoordinator: emp.trtCoordinator || '',
            trtID: emp.trtID || '',
            professionalLicenseID: emp.professionalLicenseID || '',
            employmentType: emp.employmentType || '',
            pid: emp.pid || '',
            label: emp.label || `${emp.firstName} ${emp.lastName}`,
            position: emp.position || emp.positionJob || '',
            // เพิ่มข้อมูลการอบรม
            sentBy: bulkSentBy,
            needsRoom: bulkNeedsRoom
        }));

        // เพิ่มเข้าไปกับคนเดิม (ไม่แทนที่)
        setFormData(prev => ({
            ...prev,
            selectedEmployees: [...prev.selectedEmployees, ...employeesWithDetails]
        }));

        // Reset
        setTempSelectedEmployees([]);
        setShowBulkSentByDialog(false);
        setBulkSentBy('');
        setBulkNeedsRoom(false);

        Swal.fire({
            icon: 'success',
            title: 'เพิ่มสำเร็จ',
            text: `เพิ่มพนักงาน ${employeesWithDetails.length} คน`,
            confirmButtonColor: '#10B981',
            timer: 1500,
            showConfirmButton: false
        });
    };

    const removeEmployee = (employeeId) => {
        setFormData(prev => ({
            ...prev,
            selectedEmployees: prev.selectedEmployees.filter(emp => {
                const empId = emp.id || emp._id;
                return empId !== employeeId;
            })
        }));
    };

    const validateForm = () => {
        if (!formData.title || !formData.startDate || !formData.endDate || !formData.location) {
            Swal.fire({ icon: 'warning', title: 'กรุณากรอกข้อมูลให้ครบถ้วน', text: 'กรุณากรอกหัวข้อ วันที่เริ่ม วันที่สิ้นสุด และสถานที่อบรม', confirmButtonColor: '#4F46E5' });
            return false;
        }
        if (new Date(formData.startDate) > new Date(formData.endDate)) {
            Swal.fire({ icon: 'warning', title: 'วันที่ไม่ถูกต้อง', text: 'วันที่เริ่มต้องไม่เกินวันที่สิ้นสุด', confirmButtonColor: '#4F46E5' });
            return false;
        }
        if (formData.selectedEmployees.length === 0) {
            Swal.fire({ icon: 'warning', title: 'กรุณาเลือกผู้เข้าอบรม', text: 'กรุณาเลือกพนักงานที่จะเข้าอบรมอย่างน้อย 1 คน', confirmButtonColor: '#4F46E5' });
            return false;
        }
        return true;
    };

    const handleCreateTraining = async () => {
        if (!validateForm()) return;
        setLoading(true);
        try {
            const response = await fetch(GOOGLE_APPS_SCRIPT_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'create',
                    title: formData.title,
                    startDate: formData.startDate,
                    endDate: formData.endDate,
                    location: formData.location,
                    description: formData.description,
                    mapIframe: formData.mapIframe,
                    participants: JSON.stringify(formData.selectedEmployees)
                })
            });

            const data = await response.json();

            if (data.success) {
                await loadTrainings(); // Reload data from server
                Swal.fire({
                    icon: 'success',
                    title: 'บันทึกสำเร็จ',
                    text: 'สร้างหลักสูตรอบรมเรียบร้อยแล้ว',
                    confirmButtonColor: '#10B981'
                });
                handleCloseDialogs();
            } else {
                throw new Error(data.message || 'Failed to create training');
            }
        } catch (error) {
            console.error('Error creating training:', error);
            Swal.fire({
                icon: 'error',
                title: 'เกิดข้อผิดพลาด',
                text: error.message || 'ไม่สามารถบันทึกข้อมูลได้',
                confirmButtonColor: '#EF4444'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateTraining = async () => {
        if (!validateForm()) return;
        setLoading(true);
        try {
            const response = await fetch(GOOGLE_APPS_SCRIPT_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'update',
                    id: formData.id,
                    title: formData.title,
                    startDate: formData.startDate,
                    endDate: formData.endDate,
                    location: formData.location,
                    description: formData.description,
                    mapIframe: formData.mapIframe,
                    participants: JSON.stringify(formData.selectedEmployees)
                })
            });

            const data = await response.json();

            if (data.success) {
                await loadTrainings(); // Reload data from server
                Swal.fire({
                    icon: 'success',
                    title: 'อัพเดทสำเร็จ',
                    text: 'แก้ไขข้อมูลหลักสูตรอบรมเรียบร้อยแล้ว',
                    confirmButtonColor: '#10B981'
                });
                handleCloseDialogs();
            } else {
                throw new Error(data.message || 'Failed to update training');
            }
        } catch (error) {
            console.error('Error updating training:', error);
            Swal.fire({
                icon: 'error',
                title: 'เกิดข้อผิดพลาด',
                text: error.message || 'ไม่สามารถอัพเดทข้อมูลได้',
                confirmButtonColor: '#EF4444'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTraining = async (training) => {
        if (isPastDate(training.endDate)) {
            Swal.fire({
                icon: 'error',
                title: 'ไม่สามารถลบได้',
                text: 'ไม่สามารถลบหลักสูตรที่เลยกำหนดวันอบรมแล้ว',
                confirmButtonColor: '#EF4444'
            });
            return;
        }

        const result = await Swal.fire({
            icon: 'warning',
            title: 'ยืนยันการลบ',
            text: 'คุณต้องการลบหลักสูตรอบรมนี้ใช่หรือไม่?',
            showCancelButton: true,
            confirmButtonColor: '#EF4444',
            cancelButtonColor: '#6B7280',
            confirmButtonText: 'ลบ',
            cancelButtonText: 'ยกเลิก'
        });

        if (result.isConfirmed) {
            setLoading(true);
            try {
                const response = await axios.post(GOOGLE_APPS_SCRIPT_URL,
                    new URLSearchParams({
                        action: 'delete',
                        id: training.id
                    }),
                    {
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded'
                        }
                    }
                );

                if (response.data.success) {
                    await loadTrainings(); // Reload data from server
                    Swal.fire({
                        icon: 'success',
                        title: 'ลบสำเร็จ',
                        text: 'ลบหลักสูตรอบรมเรียบร้อยแล้ว',
                        confirmButtonColor: '#10B981'
                    });
                } else {
                    throw new Error(response.data.message || 'Failed to delete training');
                }
            } catch (error) {
                console.error('Error deleting training:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'เกิดข้อผิดพลาด',
                    text: error.message || 'ไม่สามารถลบข้อมูลได้',
                    confirmButtonColor: '#EF4444'
                });
            } finally {
                setLoading(false);
            }
        }
    };

    const handleViewParticipants = (training) => { setSelectedTraining(training); setSentByFilter(''); setRoomFilter('all'); setShowViewDialog(true); };

    const exportToPDF = async (training) => {
        try {
            // Show loading
            Swal.fire({
                title: 'กำลังสร้าง PDF...',
                text: 'กรุณารอสักครู่',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            // Create temporary HTML element for rendering
            const tempDiv = document.createElement('div');
            tempDiv.style.position = 'absolute';
            tempDiv.style.left = '-9999px';
            tempDiv.style.top = '0';
            tempDiv.style.width = '800px';
            tempDiv.style.padding = '40px';
            tempDiv.style.backgroundColor = 'white';
            tempDiv.style.fontFamily = 'Arial, sans-serif';

            // Count by Sent By
            const sentByCounts = training.participants.reduce((acc, p) => {
                acc[p.sentBy] = (acc[p.sentBy] || 0) + 1;
                return acc;
            }, {});

            // Count by Room Requirement
            const needRoomCount = training.participants.filter(p => p.needsRoom).length;
            const noRoomCount = training.participants.length - needRoomCount;

            // Build HTML content in Thai
            tempDiv.innerHTML = `
                <div style="font-family: 'Sarabun', 'Tahoma', 'Arial', sans-serif;">
                    <h1 style="text-align: center; color: #1e40af; margin-bottom: 30px; font-size: 28px; font-weight: bold;">
                        รายงานผู้เข้าอบรม
                    </h1>
                    
                    <div style="margin-bottom: 25px; line-height: 2; font-size: 16px; background-color: #f3f4f6; padding: 20px; border-radius: 10px;">
                        <p style="margin: 8px 0;"><strong style="color: #374151;">หัวข้อการอบรม:</strong> ${training.title}</p>
                        <p style="margin: 8px 0;"><strong style="color: #374151;">สถานที่:</strong> ${training.location}</p>
                        <p style="margin: 8px 0;"><strong style="color: #374151;">วันที่:</strong> ${formatDateRange(training.startDate, training.endDate)}</p>
                        ${training.description ? `<p style="margin: 8px 0;"><strong style="color: #374151;">รายละเอียด:</strong> ${training.description}</p>` : ''}
                        <p style="margin: 8px 0;"><strong style="color: #374151;">จำนวนผู้เข้าอบรมทั้งหมด:</strong> ${training.participants.length} คน</p>
                        <p style="margin: 8px 0;"><strong style="color: #374151;">ส่งโดย:</strong> ${Object.entries(sentByCounts).map(([key, val]) => `${key}(${val})`).join(', ')}</p>
                        <p style="margin: 8px 0;"><strong style="color: #374151;">ต้องการห้องพัก:</strong> ต้องการ(${needRoomCount}), ไม่ต้องการ(${noRoomCount})</p>
                    </div>
                    
                    <table style="width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 13px;">
                        <thead>
                            <tr style="background-color: #4f46e5; color: white;">
                                <th style="border: 2px solid #4338ca; padding: 10px; text-align: center; font-weight: bold;">ลำดับ</th>
                                <th style="border: 2px solid #4338ca; padding: 10px; text-align: left; font-weight: bold;">ชื่อ-นามสกุล</th>
                                <th style="border: 2px solid #4338ca; padding: 10px; text-align: left; font-weight: bold;">ชื่อเล่น</th>
                                <th style="border: 2px solid #4338ca; padding: 10px; text-align: center; font-weight: bold;">เพศ</th>
                                <th style="border: 2px solid #4338ca; padding: 10px; text-align: left; font-weight: bold;">ตำแหน่ง</th>
                                <th style="border: 2px solid #4338ca; padding: 10px; text-align: left; font-weight: bold;">เบอร์โทร</th>
                                <th style="border: 2px solid #4338ca; padding: 10px; text-align: center; font-weight: bold;">ส่งโดย</th>
                                <th style="border: 2px solid #4338ca; padding: 10px; text-align: center; font-weight: bold;">ต้องการห้องพัก</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${training.participants.map((p, index) => `
                                <tr style="background-color: ${index % 2 === 0 ? '#f9fafb' : 'white'};">
                                    <td style="border: 1px solid #d1d5db; padding: 8px; text-align: center;">${index + 1}</td>
                                    <td style="border: 1px solid #d1d5db; padding: 8px;">${p.label || `${p.firstName} ${p.lastName}`}</td>
                                    <td style="border: 1px solid #d1d5db; padding: 8px;">${p.nickname || '-'}</td>
                                    <td style="border: 1px solid #d1d5db; padding: 8px; text-align: center;">${p.sex || '-'}</td>
                                    <td style="border: 1px solid #d1d5db; padding: 8px;">${p.positionJob || p.position || '-'}</td>
                                    <td style="border: 1px solid #d1d5db; padding: 8px;">${p.phone || p.phone || '-'}</td>
                                    <td style="border: 1px solid #d1d5db; padding: 8px; text-align: center;">${p.sentBy || '-'}</td>
                                    <td style="border: 1px solid #d1d5db; padding: 8px; text-align: center; font-weight: bold; color: ${p.needsRoom ? '#059669' : '#6b7280'};">${p.needsRoom ? 'ต้องการ' : 'ไม่ต้องการ'}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                    
                    <div style="margin-top: 30px; text-align: right; font-size: 13px; color: #6b7280; border-top: 2px solid #e5e7eb; padding-top: 15px;">
                        <p>วันที่สร้างรายงาน: ${new Date().toLocaleDateString('th-TH', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })}</p>
                    </div>
                </div>
            `;

            document.body.appendChild(tempDiv);

            // Convert HTML to canvas
            const canvas = await html2canvas(tempDiv, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                windowWidth: 800
            });

            // Remove temporary element
            document.body.removeChild(tempDiv);

            // Create PDF
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const imgWidth = 210; // A4 width in mm
            const pageHeight = 297; // A4 height in mm
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;
            let position = 0;

            // Add image to PDF (handle multiple pages if needed)
            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft > 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            const fileName = `รายงานการอบรม_${training.title.replace(/\s+/g, '_')}_${new Date().toLocaleDateString('th-TH').replace(/\//g, '-')}.pdf`;
            pdf.save(fileName);

            Swal.fire({
                icon: 'success',
                title: 'ส่งออกสำเร็จ',
                text: 'ดาวน์โหลดไฟล์ PDF เรียบร้อยแล้ว',
                confirmButtonColor: '#10B981',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Export error:', error);
            Swal.fire({
                icon: 'error',
                title: 'เกิดข้อผิดพลาด',
                text: 'ไม่สามารถส่งออก PDF ได้: ' + error.message,
                confirmButtonColor: '#EF4444'
            });
        }
    };

    const exportToExcelAll = async (training) => {
        try {
            // Show loading
            Swal.fire({
                title: 'กำลังสร้าง Excel...',
                text: 'กรุณารอสักครู่',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('รายชื่อผู้เข้าอบรม');

            // 1. จัดรูปแบบหัวข้อรายงาน
            worksheet.mergeCells('A1:N1');
            const mainTitle = worksheet.getCell('A1');
            mainTitle.value = 'รายงานผู้เข้าอบรม';
            mainTitle.font = { name: 'Sarabun', size: 18, bold: true, color: { argb: '1E40AF' } };
            mainTitle.alignment = { horizontal: 'center' };

            // 2. ข้อมูลรายละเอียดโครงการ
            worksheet.addRow(['หัวข้อการอบรม:', training.title]);
            worksheet.addRow(['สถานที่:', training.location]);
            worksheet.addRow(['วันที่:', formatDateRange(training.startDate, training.endDate)]);
            worksheet.addRow(['จำนวนทั้งหมด:', `${training.participants.length} คน`]);
            worksheet.addRow([]); // บรรทัดว่าง

            // จัดรูปแบบ Info Section
            ['A2', 'A3', 'A4', 'A5'].forEach(cell => {
                worksheet.getCell(cell).font = { bold: true };
            });

            // 3. สร้าง Header ของตาราง
            const headerRow = worksheet.addRow([
                'ลำดับ', 'ชื่อ-นามสกุล', 'ชื่อเล่น', 'เพศ', 'เลขบัตรประชาชน', 'ตำแหน่ง',
                'วดป. เกิด', 'วดป. เริ่มงาน', 'เลขใบประกอบ', 'เลขสภา', 'เลข ว.', 'เลขสมาคมโรคไต',
                'เบอร์โทร', 'อีเมล', 'ที่อยู่', 'ส่งโดย', 'ต้องการห้องพัก'
            ]);

            headerRow.eachCell((cell) => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: '4F46E5' }
                };
                cell.font = { color: { argb: 'FFFFFF' }, bold: true };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });

            // 4. ใส่ข้อมูลผู้เข้าอบรม
            training.participants.forEach((p, index) => {
                const rowData = [
                    index + 1,
                    p.label || `${p.firstName} ${p.lastName}`,
                    p.nickname || '-',
                    p.sex || '-',
                    p.pid || '-',
                    p.positionJob || p.position || '-',
                    p.dob || p.dob || '-',
                    p.firstDayOfWork || p.firstDayOfWork || '-',
                    p.professionalLicenseID || p.MedicalLicenseID || '-',
                    p.councilID || '-',
                    p.WorID || '-',
                    p.trtID || '-',
                    p.phone || '-',
                    p.emailNurse || '-',
                    `${p.address || ''} ${p.codeAddress || ''}`.trim() || '-',
                    p.sentBy || '-',
                    p.needsRoom ? 'ต้องการ' : 'ไม่ต้องการ'
                ];

                const row = worksheet.addRow(rowData);

                // จัดรูปแบบข้อมูลในแถว (Zebra Striping)
                if (index % 2 !== 0) {
                    row.eachCell((cell) => {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'F9FAFB' }
                        };
                    });
                }

                // ใส่เส้นขอบและจัดตำแหน่ง
                row.eachCell((cell, colNumber) => {
                    cell.border = {
                        top: { style: 'thin', color: { argb: 'D1D5DB' } },
                        left: { style: 'thin', color: { argb: 'D1D5DB' } },
                        bottom: { style: 'thin', color: { argb: 'D1D5DB' } },
                        right: { style: 'thin', color: { argb: 'D1D5DB' } }
                    };

                    // ไฮไลท์สีในคอลัมน์ "ต้องการห้องพัก"
                    if (colNumber === 14) {
                        cell.font = {
                            bold: true,
                            color: { argb: p.needsRoom ? '059669' : '6B7280' }
                        };
                        cell.alignment = { horizontal: 'center' };
                    }
                });
            });

            // 5. ปรับความกว้างคอลัมน์อัตโนมัติ (โดยประมาณ)
            const widths = [8, 25, 10, 20, 25, 15, 15, 12, 15, 15, 25, 40, 20, 15];
            worksheet.columns.forEach((col, i) => {
                col.width = widths[i];
            });

            // 6. บันทึกไฟล์
            const buffer = await workbook.xlsx.writeBuffer();
            const fileName = `รายงานการอบรม_${training.title.replace(/\s+/g, '_')}_${new Date().toLocaleDateString('th-TH').replace(/\//g, '-')}.xlsx`;

            saveAs(new Blob([buffer]), fileName);

            Swal.fire({
                icon: 'success',
                title: 'ส่งออกสำเร็จ',
                text: 'ดาวน์โหลดไฟล์ Excel เรียบร้อยแล้ว',
                confirmButtonColor: '#10B981',
                timer: 2000,
                showConfirmButton: false
            });

        } catch (error) {
            console.error('Export Excel error:', error);
            Swal.fire({
                icon: 'error',
                title: 'เกิดข้อผิดพลาด',
                text: 'ไม่สามารถส่งออก Excel ได้: ' + error.message,
                confirmButtonColor: '#EF4444'
            });
        }
    };

    const getTrainingStatus = (startDate, endDate) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setHours(0, 0, 0, 0);

        if (today > end) return 'finished'; // สิ้นสุด
        if (today >= start && today <= end) return 'ongoing'; // กำลังดำเนินการ
        return 'upcoming'; // กำลังจะมาถึง
    };

    const isPastDate = (dateString) => { const trainingDate = new Date(dateString); const today = new Date(); today.setHours(0, 0, 0, 0); return trainingDate < today; };
    const formatDateRange = (startDate, endDate) => {
        const start = new Date(startDate).toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const end = new Date(endDate).toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit', year: 'numeric' });
        return startDate === endDate ? start : `${start} - ${end}`;
    };

    const calculateWorkExperience = (firstDayOfWork) => {
        if (!firstDayOfWork) return '-';
        const startDate = new Date(firstDayOfWork);
        const today = new Date();

        let years = today.getFullYear() - startDate.getFullYear();
        let months = today.getMonth() - startDate.getMonth();
        let days = today.getDate() - startDate.getDate();

        if (days < 0) {
            months--;
            const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
            days += lastMonth.getDate();
        }

        if (months < 0) {
            years--;
            months += 12;
        }

        const parts = [];
        if (years > 0) parts.push(`${years} ปี`);
        if (months > 0) parts.push(`${months} เดือน`);
        if (days > 0) parts.push(`${days} วัน`);

        return parts.length > 0 ? parts.join(' ') : '0 วัน';
    };
    const totalParticipants = trainings.reduce((sum, training) => sum + training.participants.length, 0);
    const upcomingTrainings = trainings.filter(t => !isPastDate(t.endDate)).length;

    return (
        <div className="w-full min-h-dvh bg-gradient-to-br from-gray-50 to-gray-100 py-8 relative">
            {/* Loading Overlay */}
            {loading && (
                <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center gap-4">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600"></div>
                        <p className="text-gray-700 font-medium">กำลังโหลด...</p>
                    </div>
                </div>
            )}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg">
                                <AcademicCapIcon className="h-8 w-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">จัดการอบรม</h1>
                                <p className="text-gray-500 mt-1">สร้างและจัดการหลักสูตรอบรมพนักงาน</p>
                            </div>
                        </div>
                        <button onClick={handleOpenCreateDialog} className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105">
                            <PlusIcon className="h-5 w-5" />สร้างหลักสูตรอบรม
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100">
                        <div className="flex items-center justify-between">
                            <div><p className="text-sm text-gray-500 font-medium">หลักสูตรทั้งหมด</p><p className="text-3xl font-bold text-gray-900 mt-2">{trainings.length}</p></div>
                            <div className="p-4 bg-blue-50 rounded-xl"><AcademicCapIcon className="h-8 w-8 text-blue-600" /></div>
                        </div>
                    </div>
                    <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100">
                        <div className="flex items-center justify-between">
                            <div><p className="text-sm text-gray-500 font-medium">ผู้เข้าอบรมทั้งหมด</p><p className="text-3xl font-bold text-gray-900 mt-2">{totalParticipants}</p></div>
                            <div className="p-4 bg-green-50 rounded-xl"><UserGroupIcon className="h-8 w-8 text-green-600" /></div>
                        </div>
                    </div>
                    <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100">
                        <div className="flex items-center justify-between">
                            <div><p className="text-sm text-gray-500 font-medium">หลักสูตรที่กำลังจะมาถึง</p><p className="text-3xl font-bold text-gray-900 mt-2">{upcomingTrainings}</p></div>
                            <div className="p-4 bg-purple-50 rounded-xl"><CalendarIcon className="h-8 w-8 text-purple-600" /></div>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold">หลักสูตร</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold">วันที่อบรม</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold">สถานที่</th>
                                    <th className="px-6 py-4 text-center text-sm font-semibold">ผู้เข้าอบรม</th>
                                    <th className="px-6 py-4 text-center text-sm font-semibold">สถานะ</th>
                                    <th className="px-6 py-4 text-center text-sm font-semibold">จัดการ</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {trainings.map((training, index) => {
                                    const status = getTrainingStatus(training.startDate, training.endDate);
                                    const isExpired = isPastDate(training.endDate);

                                    const statusConfig = {
                                        upcoming: { text: 'กำลังจะมาถึง', bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
                                        ongoing: { text: 'กำลังดำเนินการ', bgColor: 'bg-green-100', textColor: 'text-green-700' },
                                        finished: { text: 'สิ้นสุด', bgColor: 'bg-gray-100', textColor: 'text-gray-600' }
                                    };

                                    return (
                                        <tr key={training.id} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                            <td className="px-6 py-4">
                                                <div>
                                                    <p className="font-semibold text-gray-900 text-base">{training.title}</p>
                                                    {training.description && (
                                                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{training.description}</p>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2 text-gray-700">
                                                    <CalendarIcon className="h-5 w-5 text-indigo-500" />
                                                    <span className="text-sm font-medium">{formatDateRange(training.startDate, training.endDate)}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-start gap-2 text-gray-700">
                                                    <MapPinIcon className="h-5 w-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                                                    <span className="text-sm">{training.location}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <UserGroupIcon className="h-5 w-5 text-green-500" />
                                                    <span className="text-lg font-bold text-green-600">{training.participants.length}</span>
                                                    <span className="text-sm text-gray-500">คน</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusConfig[status].bgColor} ${statusConfig[status].textColor}`}>
                                                    {statusConfig[status].text}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => handleViewParticipants(training)}
                                                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                                        title="ดูรายชื่อ"
                                                    >
                                                        <EyeIcon className="h-5 w-5" />
                                                    </button>
                                                    {/* <button
                                                        onClick={() => exportToPDF(training)}
                                                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                                        title="ส่งออก PDF"
                                                    >
                                                        <DocumentArrowDownIcon className="h-5 w-5" />
                                                    </button> */}
                                                    {!isExpired && (
                                                        <>
                                                            <button
                                                                onClick={() => handleOpenEditDialog(training)}
                                                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                                title="แก้ไข"
                                                            >
                                                                <PencilIcon className="h-5 w-5" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteTraining(training)}
                                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                                title="ลบ"
                                                            >
                                                                <TrashIcon className="h-5 w-5" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {trainings.length === 0 && !loading && (
                    <div className="text-center py-16">
                        <AcademicCapIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 text-lg">ยังไม่มีหลักสูตรอบรม</p>
                        <p className="text-gray-400 text-sm mt-2">คลิกปุ่ม "สร้างหลักสูตรอบรม" เพื่อเริ่มต้น</p>
                        {GOOGLE_APPS_SCRIPT_URL === 'YOUR_DEPLOYED_GOOGLE_APPS_SCRIPT_URL_HERE' && (
                            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg max-w-2xl mx-auto">
                                <p className="text-yellow-800 font-medium">⚠️ กรุณาตั้งค่า API URL</p>
                                <p className="text-yellow-700 text-sm mt-2">
                                    แก้ไขค่า GOOGLE_APPS_SCRIPT_URL ในไฟล์ TrainingManagement.jsx<br />
                                    ดูวิธีการใน google-apps-script/README.md
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Create/Edit Dialog */}
            {(showCreateDialog || showEditDialog) && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-hidden">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
                        <div className="bg-white border-b border-gray-200 p-6 flex items-center justify-between rounded-t-2xl flex-shrink-0">
                            <h2 className="text-2xl font-bold text-gray-900">{showCreateDialog ? 'สร้างหลักสูตรอบรม' : 'แก้ไขหลักสูตรอบรม'}</h2>
                            <button onClick={handleCloseDialogs} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><XMarkIcon className="h-6 w-6 text-gray-500" /></button>
                        </div>
                        <div className="p-6 space-y-6 overflow-y-auto flex-1">
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-gray-900">ข้อมูลหลักสูตร</h3>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">หัวข้ออบรม <span className="text-red-500">*</span></label>
                                    <input type="text" value={formData.title} onChange={(e) => handleInputChange('title', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" placeholder="ระบุหัวข้ออบรม" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">วันที่เริ่มอบรม <span className="text-red-500">*</span></label>
                                        <input type="date" value={formData.startDate} onChange={(e) => handleInputChange('startDate', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">วันที่สิ้นสุดอบรม <span className="text-red-500">*</span></label>
                                        <input type="date" value={formData.endDate} onChange={(e) => handleInputChange('endDate', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">สถานที่อบรม <span className="text-red-500">*</span></label>
                                    <input type="text" value={formData.location} onChange={(e) => handleInputChange('location', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" placeholder="ระบุสถานที่" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">รายละเอียด</label>
                                    <textarea value={formData.description} onChange={(e) => handleInputChange('description', e.target.value)} rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" placeholder="ระบุรายละเอียดเพิ่มเติม" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Google Maps Iframe Code</label>
                                    <textarea value={formData.mapIframe} onChange={(e) => handleInputChange('mapIframe', e.target.value)} rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm" placeholder='<iframe src="https://www.google.com/maps/embed?pb=..." width="600" height="450" style="border:0;" allowfullscreen="" loading="lazy"></iframe>' />
                                    <p className="text-xs text-gray-500 mt-1">วิธีการ: เปิด Google Maps → แชร์ → ฝังแผนที่ → คัดลอก iframe code ทั้งหมด</p>
                                    {formData.mapIframe && (
                                        <div className="mt-3 rounded-lg overflow-hidden border border-gray-200" dangerouslySetInnerHTML={{ __html: formData.mapIframe.replace(/width="[^"]*"/, 'width="100%"').replace(/height="[^"]*"/, 'height="300"') }} />
                                    )}
                                </div>
                            </div>
                            <div className="space-y-4 border-t border-gray-200 pt-6">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-semibold text-gray-900">เลือกผู้เข้าอบรม <span className="text-red-500">*</span></h3>
                                    <span className="text-sm text-gray-500">เลือกแล้ว {formData.selectedEmployees.length} คน</span>
                                </div>
                                <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" placeholder="ค้นหาพนักงาน..." />
                                </div>

                                <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                                    {filteredEmployees.length === 0 && (
                                        <div className="p-8 text-center text-gray-500">
                                            <p>ไม่พบข้อมูลพนักงาน</p>
                                            <p className="text-sm mt-2">กรุณาตรวจสอบการเชื่อมต่อ API</p>
                                        </div>
                                    )}
                                    {filteredEmployees.map(employee => {
                                        const employeeId = employee.id || employee._id;

                                        // ตรวจสอบว่าเลือกใน temp หรือยัง
                                        const isSelected = tempSelectedEmployees.some(emp => {
                                            const empId = emp.id || emp._id;
                                            return empId === employeeId;
                                        });

                                        // ตรวจสอบว่าเพิ่มไปแล้วหรือยัง
                                        const isAdded = formData.selectedEmployees.some(emp => {
                                            const empId = emp.id || emp._id;
                                            return empId === employeeId;
                                        });
                                        const workExp = calculateWorkExperience(employee.firstDayOfWork);
                                        return (
                                            <div
                                                key={employee.id || employee._id}
                                                onClick={() => !isAdded && toggleEmployeeSelection(employee)}
                                                className={`p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${isAdded ? 'bg-green-50 opacity-50 cursor-not-allowed' :
                                                    isSelected ? 'bg-indigo-50' : ''
                                                    }`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex-1">
                                                        <p className="font-medium text-gray-900">{employee.firstName} {employee.lastName}</p>
                                                        <p className="text-sm text-gray-600 mt-0.5">{employee.positionJob || employee.position || '-'}</p>
                                                        <p className="text-xs text-indigo-600 mt-1">อายุงาน: {workExp}</p>
                                                    </div>
                                                    {isAdded && (
                                                        <div className="h-5 w-5 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 ml-2">
                                                            <svg className="h-3 w-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor"><path d="M5 13l4 4L19 7"></path></svg>
                                                        </div>
                                                    )}
                                                    {!isAdded && isSelected && (
                                                        <div className="h-5 w-5 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0 ml-2">
                                                            <svg className="h-3 w-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor"><path d="M5 13l4 4L19 7"></path></svg>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* ปุ่มเพิ่มพนักงานที่เลือก */}
                                {tempSelectedEmployees.length > 0 && (
                                    <div className="p-4 bg-indigo-50 border-t-2 border-indigo-200">
                                        <button
                                            onClick={handleOpenBulkSentByDialog}
                                            className="w-full px-4 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors font-semibold flex items-center justify-center gap-2"
                                        >
                                            <PlusIcon className="h-5 w-5" />
                                            เพิ่มพนักงาน {tempSelectedEmployees.length} คน
                                        </button>
                                    </div>
                                )}

                                {formData.selectedEmployees.length > 0 && (
                                    <div className="bg-indigo-50 rounded-lg p-4">
                                        <p className="text-sm font-medium text-indigo-900 mb-2">พนักงานที่เลือก:</p>
                                        <div className="space-y-2">
                                            {formData.selectedEmployees.map(emp => {
                                                const empId = emp.id || emp._id;
                                                return (
                                                    <div key={empId} className="flex items-center justify-between bg-white px-4 py-2 rounded-lg border border-indigo-200">
                                                        <div className="flex-1">
                                                            <p className="text-sm font-medium text-gray-900">{emp.label || `${emp.firstName} ${emp.lastName}`}</p>
                                                            <div className="flex items-center gap-3 mt-1">
                                                                <p className="text-xs text-gray-500">ส่งโดย: {emp.sentBy || '-'}</p>
                                                                <span className="text-gray-300">|</span>
                                                                <p className={`text-xs font-medium ${emp.needsRoom ? 'text-green-600' : 'text-gray-500'}`}>
                                                                    ห้องพัก: {emp.needsRoom ? 'ต้องการ' : 'ไม่ต้องการ'}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <button onClick={() => removeEmployee(empId)} className="text-red-500 hover:text-red-700 ml-2"><XMarkIcon className="h-5 w-5" /></button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                            </div>
                        </div>
                        <div className="bg-white border-t border-gray-200 p-6 flex gap-3 rounded-b-2xl flex-shrink-0">
                            <button
                                onClick={handleCloseDialogs}
                                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
                                disabled={loading}
                            >
                                ยกเลิก
                            </button>
                            <button
                                onClick={showCreateDialog ? handleCreateTraining : handleUpdateTraining}
                                className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={loading}
                            >
                                {loading ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Participants Dialog */}
            {showViewDialog && selectedTraining && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-hidden">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                        <div className="p-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                            <h2 className="text-2xl font-bold text-gray-900">รายชื่อผู้เข้าอบรม</h2>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => exportToExcelAll(selectedTraining)}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
                                >
                                    <DocumentArrowDownIcon className="h-5 w-5" />
                                    Excel
                                </button>
                                <button
                                    onClick={() => exportToPDF(selectedTraining)}
                                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-md"
                                >
                                    <DocumentArrowDownIcon className="h-5 w-5" />
                                    PDF
                                </button>
                                <button onClick={() => setShowViewDialog(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                    <XMarkIcon className="h-6 w-6 text-gray-500" />
                                </button>
                            </div>
                        </div>
                        <div className="p-6 space-y-4 overflow-y-auto flex-1">
                            <div className="bg-indigo-50 rounded-xl p-4 mb-4">
                                <p className="text-sm text-indigo-600 font-medium">หลักสูตร</p>
                                <p className="text-lg font-bold text-indigo-900">{selectedTraining.title}</p>
                            </div>

                            {/* Summary by SentBy */}
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-4">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-sm font-semibold text-gray-700">สรุปจำนวนผู้เข้าอบรมแยกตามหน่วยงาน</p>
                                    {(sentByFilter || roomFilter !== 'all') && (
                                        <button
                                            onClick={() => { setSentByFilter(''); setRoomFilter('all'); }}
                                            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                                        >
                                            ล้างฟิลเตอร์ทั้งหมด
                                        </button>
                                    )}
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    {(() => {
                                        const sentByCount = selectedTraining.participants.reduce((acc, p) => {
                                            const sentBy = p.sentBy || 'ไม่ระบุ';
                                            acc[sentBy] = (acc[sentBy] || 0) + 1;
                                            return acc;
                                        }, {});
                                        return Object.entries(sentByCount).map(([sentBy, count]) => (
                                            <button
                                                key={sentBy}
                                                onClick={() => setSentByFilter(sentByFilter === sentBy ? '' : sentBy)}
                                                className={`flex items-center justify-between px-3 py-2 rounded-lg border transition-all ${sentByFilter === sentBy
                                                    ? 'bg-indigo-600 border-indigo-600 text-white shadow-md'
                                                    : 'bg-white border-indigo-100 hover:border-indigo-300 hover:shadow-sm'
                                                    }`}
                                            >
                                                <span className={`text-sm font-medium ${sentByFilter === sentBy ? 'text-white' : 'text-gray-700'}`}>{sentBy}</span>
                                                <span className={`text-sm font-bold ${sentByFilter === sentBy ? 'text-white' : 'text-indigo-600'}`}>{count} คน</span>
                                            </button>
                                        ));
                                    })()}
                                </div>
                            </div>

                            {/* Room Filter */}
                            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 mb-4">
                                <p className="text-sm font-semibold text-gray-700 mb-3">ฟิลเตอร์ตามความต้องการห้องพัก</p>
                                <div className="grid grid-cols-3 gap-2">
                                    <button
                                        onClick={() => setRoomFilter('all')}
                                        className={`px-3 py-2 rounded-lg border transition-all ${roomFilter === 'all'
                                            ? 'bg-gray-600 border-gray-600 text-white shadow-md'
                                            : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                                            }`}
                                    >
                                        <span className={`text-sm font-medium ${roomFilter === 'all' ? 'text-white' : 'text-gray-700'}`}>ทั้งหมด</span>
                                    </button>
                                    <button
                                        onClick={() => setRoomFilter('needs')}
                                        className={`px-3 py-2 rounded-lg border transition-all ${roomFilter === 'needs'
                                            ? 'bg-green-600 border-green-600 text-white shadow-md'
                                            : 'bg-white border-green-100 hover:border-green-300 hover:shadow-sm'
                                            }`}
                                    >
                                        <span className={`text-sm font-medium ${roomFilter === 'needs' ? 'text-white' : 'text-gray-700'}`}>ต้องการ ({selectedTraining.participants.filter(p => p.needsRoom).length})</span>
                                    </button>
                                    <button
                                        onClick={() => setRoomFilter('no-needs')}
                                        className={`px-3 py-2 rounded-lg border transition-all ${roomFilter === 'no-needs'
                                            ? 'bg-orange-600 border-orange-600 text-white shadow-md'
                                            : 'bg-white border-orange-100 hover:border-orange-300 hover:shadow-sm'
                                            }`}
                                    >
                                        <span className={`text-sm font-medium ${roomFilter === 'no-needs' ? 'text-white' : 'text-gray-700'}`}>ไม่ต้องการ ({selectedTraining.participants.filter(p => !p.needsRoom).length})</span>
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                {selectedTraining.participants
                                    .filter(p => !sentByFilter || p.sentBy === sentByFilter)
                                    .filter(p => {
                                        if (roomFilter === 'all') return true;
                                        if (roomFilter === 'needs') return p.needsRoom === true;
                                        if (roomFilter === 'no-needs') return p.needsRoom === false;
                                        return true;
                                    })
                                    .map((participant, index) => (
                                        <div key={participant.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-all">
                                            <div className="flex items-start gap-4">
                                                <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold flex-shrink-0">
                                                    {index + 1}
                                                </div>
                                                <div className="flex-1 space-y-2">
                                                    <div>
                                                        <p className="font-bold text-gray-900 text-lg">{participant.label}</p>
                                                        {participant.nickname && <p className="text-sm text-gray-500">({participant.nickname})</p>}
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                                        <div>
                                                            <span className="text-gray-500">เลขบัตรประชาชน:</span>
                                                            <span className="ml-2 text-gray-900 font-medium">{participant.pid || '-'}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500">ตำแหน่ง:</span>
                                                            <span className="ml-2 text-gray-900 font-medium">{participant.position || '-'}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500">เพศ:</span>
                                                            <span className="ml-2 text-gray-900">{participant.sex || '-'}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500">เบอร์โทร:</span>
                                                            <span className="ml-2 text-gray-900">{participant.phone || '-'}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500">อีเมล:</span>
                                                            <span className="ml-2 text-gray-900 text-xs">{participant.emailNurse || '-'}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500">ประเภทการจ้าง:</span>
                                                            <span className="ml-2 text-gray-900">{participant.employmentType || '-'}</span>
                                                        </div>
                                                        {participant.MedicalLicenseID && (
                                                            <div>
                                                                <span className="text-gray-500">เลขที่ใบอนุญาตแพทย์:</span>
                                                                <span className="ml-2 text-gray-900">{participant.MedicalLicenseID}</span>
                                                            </div>
                                                        )}
                                                        {participant.WorID && (
                                                            <div>
                                                                <span className="text-gray-500">เลข ว.:</span>
                                                                <span className="ml-2 text-gray-900">{participant.WorID}</span>
                                                            </div>
                                                        )}
                                                        {participant.professionalLicenseID && (
                                                            <div>
                                                                <span className="text-gray-500">เลขที่ใบประกอบวิชาชีพ:</span>
                                                                <span className="ml-2 text-gray-900">{participant.professionalLicenseID}</span>
                                                            </div>
                                                        )}
                                                        {participant.councilID && (
                                                            <div>
                                                                <span className="text-gray-500">เลขที่ใบอนุญาตสภา:</span>
                                                                <span className="ml-2 text-gray-900">{participant.councilID}</span>
                                                            </div>
                                                        )}
                                                        {participant.trtID && (
                                                            <div>
                                                                <span className="text-gray-500">เลขที่สมาคมโรคไต:</span>
                                                                <span className="ml-2 text-gray-900">{participant.trtID}</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                                                        <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                                                            ส่งโดย: {participant.sentBy || '-'}
                                                        </span>
                                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${participant.needsRoom
                                                            ? 'bg-green-100 text-green-700'
                                                            : 'bg-gray-100 text-gray-700'
                                                            }`}>
                                                            ห้องพัก: {participant.needsRoom ? 'ต้องการ' : 'ไม่ต้องการ'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                            <div className="pt-4 border-t border-gray-200">
                                <p className="text-center text-gray-500">
                                    {(() => {
                                        const filtered = selectedTraining.participants
                                            .filter(p => !sentByFilter || p.sentBy === sentByFilter)
                                            .filter(p => {
                                                if (roomFilter === 'all') return true;
                                                if (roomFilter === 'needs') return p.needsRoom === true;
                                                if (roomFilter === 'no-needs') return p.needsRoom === false;
                                                return true;
                                            });

                                        if (sentByFilter || roomFilter !== 'all') {
                                            return <>แสดง <span className="font-bold text-indigo-600">{filtered.length}</span> คน {sentByFilter && `จาก ${sentByFilter}`} {roomFilter !== 'all' && `(${roomFilter === 'needs' ? 'ต้องการห้องพัก' : 'ไม่ต้องการห้องพัก'})`}</>;
                                        }
                                        return <>รวมทั้งหมด <span className="font-bold text-indigo-600">{selectedTraining.participants.length}</span> คน</>;
                                    })()}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk SentBy Dialog */}
            {showBulkSentByDialog && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                        <div className="p-6 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-gray-900">กรอกข้อมูลสำหรับพนักงานที่เลือก</h2>
                            <p className="text-sm text-gray-500 mt-1">จำนวน {tempSelectedEmployees.length} คน</p>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    ส่งโดย <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={bulkSentBy}
                                    onChange={(e) => setBulkSentBy(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                                >
                                    <option value="">-- เลือกหน่วยงาน --</option>
                                    <option value="ES">ES</option>
                                    <option value="FN">FN</option>
                                    <option value="FN2">FN2</option>
                                    <option value="He+">He+</option>
                                    <option value="NK">NK</option>
                                    <option value="Epiao">Epiao</option>
                                    <option value="Re">Re</option>
                                </select>
                            </div>
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="bulkNeedsRoom"
                                    checked={bulkNeedsRoom}
                                    onChange={(e) => setBulkNeedsRoom(e.target.checked)}
                                    className="h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                />
                                <label htmlFor="bulkNeedsRoom" className="text-sm font-medium text-gray-700">
                                    ต้องการห้องพัก
                                </label>
                            </div>
                        </div>
                        <div className="bg-gray-50 px-6 py-4 flex gap-3 rounded-b-2xl">
                            <button
                                onClick={() => setShowBulkSentByDialog(false)}
                                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                            >
                                ยกเลิก
                            </button>
                            <button
                                onClick={handleConfirmBulkSentBy}
                                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                            >
                                เพิ่มทั้งหมด
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default TrainingManagement;